import { Button, Col, Form, Input, message, Row, Slider, Tabs, Upload } from "antd"
import HeaderSection from "../components/HeaderSection"
import { Component, useEffect, useState } from "react"
import { useForm } from "antd/es/form/Form"
import { generateLeadsAI } from "../lib/generateLeadsAI"
import {DeleteOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom"
import { validateListName } from "../lib/validateListName"
import posthog from "posthog-js"
import InputIcon from '../assets/svg/inputIcon.svg'
import PlusIcon from "../assets/svg/plus.svg";
import MinusIcon from "../assets/svg/minus.svg";
import { useDebouncedPostHogCapture } from "../lib/postHogHelper.ts";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import InputMask from "react-input-mask";
import RequirePermissions from "../components/VerifyAccess.tsx"
import LocalityFilter from "../components/leads/leads-generation/locality/index.tsx"
import LegalFinancesFilter from "../components/leads/leads-generation/legal-finances/index.tsx"
import ActivitiesFilter from "../components/leads/leads-generation/activities/index.tsx"

interface Weight{
    label: string,
    name: string,
    value: number
}

const defaultWeights: Weight[] = [
    { label: 'Município', name: 'cod_mun', value: 1 },
    { label: 'UF', name: 'uf', value: 1 },
    { label: 'Região', name: 'regiao', value: 1 },
    { label: 'Natureza Jurídica', name: 'cod_natjur', value: 1 },
    { label: 'Porte', name: 'porte', value: 1 },
    { label: 'Faixa capital social', name: 'capital_social_faixa', value: 1 },
    { label: 'Número capital social', name: 'capital_social_numero', value: 1 },
    { label: 'Faixa população', name: 'faixa_populacao', value: 1 },
    { label: 'População', name: 'populacao_2022', value: 1 },
    { label: 'Faixa PIB', name: 'faixa_pib', value: 1 },
    { label: 'Faixa PIB per capita', name: 'faixa_pib_per_capita', value: 1 },
    { label: 'Opção simples', name: 'opcao_simples', value: 1 },
    { label: 'Opção MEI', name: 'opcao_mei', value: 1 },
    { label: 'Grupo', name: 'grupo', value: 1 },
    { label: 'Subclasse', name: 'subclasse', value: 1 },
    { label: 'Faixa renda', name: 'faixa_renda', value: 1 },
    { label: 'Quantidade de socios', name: 'qtd_socios', value: 1 },
    { label: 'Quantidade de estabelecimentos', name: 'qtd_estabelecimentos', value: 1 }
];

interface GenerateLeadsAIFormProps {
  upload: boolean;
  keyId: string;
}

const GenerateLeadsAIForm: React.FC<GenerateLeadsAIFormProps> = ({ upload }) => {
    const [form] = useForm()
    const debouncedPostHogCapture = useDebouncedPostHogCapture();
    const [loading, setLoading] = useState<boolean>(false)
    const [cnpjs, setCnpjs] = useState<string[]>([''])
    const [qtdLeads, setQtdLeads] = useState<number>(1)
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [filtersValues, setFiltersValues] = useState<object| undefined>(undefined)
    const [weights, setWeights] = useState<Weight[]>(defaultWeights)
    const navigate = useNavigate();

  useEffect(() => {
    if (!selectedFile) return;

    const ext = selectedFile.name.split(".").pop().toLowerCase();

    const extractCNPJs = (rows) => {
      return rows
        .map((row) => {
          const cnpjKey = Object.keys(row).find(
            (key) => key.toLowerCase() === "cnpj"
          );
          return cnpjKey
            ? Number(
                String(row[cnpjKey])
                  .replaceAll(".", "")
                  .replaceAll("/", "")
                  .replaceAll("-", "")
              )
            : null;
        })
        .filter(Boolean).slice(0,100);
    };

    if (ext === "csv") {
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCnpjs(extractCNPJs(results.data));
        },
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setCnpjs(extractCNPJs(jsonData));
      };
      reader.readAsArrayBuffer(selectedFile);
    } else {
      console.error("Formato de arquivo não suportado:", ext);
    }
  }, [selectedFile]);

    const handleSubmit = ()=>{
        form.validateFields().then(_=>{   
            setLoading(true)
            validateListName(form.getFieldValue('nome')).then(validName=>{
                if (validName?.status === undefined){
                    message.error("Erro ao validar nome da lista, por favor tente mais tarde");
                    setLoading(false)
                    return;
                }
                if(!validName?.status){
                    message.error(validName.message);
                    setLoading(false)
                    return
                }

                const filteredValues = filtersValues ? Object.fromEntries(Object.entries(filtersValues).filter(([_, value]) => Array.isArray(value) && value.length)) : {}
                
                generateLeadsAI(cnpjs.map(cnpj=>(Number(cnpj))),qtdLeads,form.getFieldValue('nome'), Object.fromEntries(weights.map(w => [w.name, w.value])), filteredValues ?? {}).then(response=>{
                    setLoading(false)

                    if(response.statusCode === 202){
                        navigate("/painel/historico-de-importacoes")
                    }
                    else{
                        message.error(JSON.stringify(response))
                    }
                })
            })

        }).catch((errorInfo) => {
            console.log("Erro na validação:", errorInfo);
        });
    }

    const uploadSection = ()=>(
        <Form.Item
            name="file"
            getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                    return e;
                }
                return e && e.fileList;
            }}
            valuePropName="fileList"
            style={{ marginBottom: 0, marginTop: '2em' }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Upload
                    accept=".csv,.xlsx"
                    beforeUpload={(file) => {
                        posthog.capture('fazer_upload_botao');
                        setSelectedFile(file);
                        return false;
                    }}
                    onRemove={() => {
                        setSelectedFile(null);
                    }}
                    showUploadList={false}
                >
                    <Button icon={<UploadOutlined />} className="LeadGenerationInput">
                        {selectedFile ? selectedFile.name : 'Fazer Upload'}
                    </Button>
                </Upload>
                <span style={{ fontSize: 'small', marginLeft: '2em', color: '#494CA8' }}>São permitidos até 100 CNPJs</span>
            </div>
        </Form.Item>
    )

    const manualSection = ()=>(
        <div style={{width: '50vh'}}>
            {
                cnpjs.map((cnpj,index)=>(
                    <Row style={{display: 'flex', alignItems: 'flex-end'}}>
                        <Form.Item label='CNPJ' style={{ width: '90%'}}  rules={[{ required: true, message: "Campo obrigatório"}]}>
                            <InputMask mask='99.999.999/9999-99' maskChar="" value={cnpj}
                                onChange={(e)=>{
                                    setCnpjs(cnpjs.map((innerCnpj,innerIndex)=>{
                                        if(innerIndex !== index) return innerCnpj
                                        return e.target.value.replaceAll('.','').replaceAll('/','').replaceAll('-','')
                                    }))
                                }}
                            >
                                {()=>(<Input placeholder="Digite um CNPJ" />)}                   
                            </InputMask>
                        </Form.Item>
                        {index ? 
                            <DeleteOutlined style={{color: '#fe6556',marginLeft:'1em', marginBottom:'0.5em'}} onClick={()=>{setCnpjs(cnpjs.filter((_,innerIndex)=>(innerIndex !== index)))}}/> 
                            : <></>
                        }
                    </Row>
                ))
            }
            <Button icon={<PlusOutlined />} style={{width: '50vh', marginTop: '2em', display: cnpjs.length >=100 ? 'none' : ''}}  type="dashed" onClick={()=>{
                setCnpjs([...cnpjs,''])
            }}>
                Adicionar CNPJ
            </Button>
        </div>
    )

    return(
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <Form form={form} layout="vertical" style={{ width: '150vh'}}>
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                    <Col style={{display: 'flex', flexDirection:'row', alignItems:'flex-end', justifyContent: 'center'}}>
                        <Form.Item
                            name={"nome"}
                            rules={[
                                { required: true, message: "Insira um nome" },
                                {
                                pattern: /^[a-zA-Z0-9_-]+$/,
                                message: "Apenas letras, números - e _ são permitidos",
                                },
                            ]}
                            style={{ marginBottom: 0, marginTop: '2em' }}
                            >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Input
                                type="text"
                                className="LeadGenerationInput"
                                placeholder="Nome da lista"
                                onBlur={() =>
                                    posthog.capture("insirar_nome_gerancao_leads_input")
                                }
                                suffix={<InputIcon />}
                                />
                                <span style={{ fontSize: 'small', marginLeft: '2em', color: '#494CA8' }}>São permitidos letras, números, - e _</span>
                            </div>
                        </Form.Item>
                    </Col>
                    <Col style={{display: 'flex', flexDirection:'row', alignItems:'flex-end', justifyContent: 'center'}}>
                        <Form.Item
                            name={"quantidade"}
                            label={"Quantidade de Leads"}
                            className="aiGenerationQtdInput"
                            colon={false}
                            initialValue={qtdLeads}
                            style={{width: '25vh', margin: '0 2em'}}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                            <Input
                                className="numericInput"
                                prefix={<MinusIcon style={{ cursor: 'pointer', transform: 'scale(1.5)', margin: '0 1em' }} onClick={() => { qtdLeads > 10 ? setQtdLeads(qtdLeads - 10) : setQtdLeads(1) }} onMouseDown={e => e.preventDefault()} />}
                                suffix={<PlusIcon style={{ cursor: 'pointer', transform: 'scale(1.5)', margin: '0 1em' }} onClick={() => { qtdLeads < 990 ? qtdLeads === 1 ? setQtdLeads(10) : setQtdLeads(qtdLeads + 10) : setQtdLeads(1000) }} onMouseDown={e => e.preventDefault()} />}
                                type="number"
                                value={qtdLeads}
                                onChange={(value) => {
                                    debouncedPostHogCapture('alterar_quantidade_leads_slider');
                                    const limitedValue = Math.max(1, Math.min(Number(value.target.value), 1000));
                                    setQtdLeads(limitedValue);
                                }}
                                maxLength={4}
                                max={1000}
                            />
                            <span className="numericInputFooter">
                                1 até 1000
                            </span>
                            </div>
                        </Form.Item>
                    </Col>
                    <Col style={{display: 'flex', flexDirection:'row', alignItems:'flex-end', justifyContent: 'center'}}>
                    {upload ? uploadSection() : manualSection()}
                    </Col>
                </div>
                <div style={{marginBottom: '2em'}}>
                    <div style={{fontSize: 'large', color: '#141774', marginTop: '2em'}}>Ajuste de input</div>
                    <div className="aiGenerationFilters">
                        
                        <LocalityFilter onChange = {(field: string, value: string | string[])=>{setFiltersValues(prev=>({...prev,[field.replace('municipio','cod_mun')]: value})); console.log(field + ' ' + value);}} aiGeneration/>
                        <div style={{display: 'flex', flexDirection: 'row'}}>
                            <LegalFinancesFilter onChange = {(field: string, value: string | string[])=>{setFiltersValues(prev=>({...prev,[field]: value}))}} aiGeneration/>
                            <ActivitiesFilter onChange = {(field: string, value: string | string[])=>{setFiltersValues(prev=>({...prev,[field]: value}))}} aiGeneration/>
                        </div>
                    </div>
                </div>
                <div>
                    <div style={{fontSize: 'large', color: '#141774'}}>Pesos</div>
                    <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: '1em'}}>
                        {
                            weights.map(weight=>(
                                <div style={{width: '30%'}}>
                                    <div style={{color: '#141774'}}>{weight.label}</div>
                                    <Slider min={0} max={5} defaultValue={weight.value} onChange={(value)=>setWeights(weights.map(w=>w.name === weight.name ? {label: w.label, name: w.name, value: value} : w))}/>
                                </div>
                            ))
                        }
                    </div>
                </div>
                <Row style={{display: 'flex', flexDirection:'row', alignItems:'flex-end', justifyContent: 'center'}}>
                    <Form.Item style={{marginTop: '2em'}}>
                        <Button
                            className="LeadGenerationButton"
                            loading={loading}
                            onClick={() => {
                                posthog.capture("gerar_leads_botao");
                                handleSubmit();
                            }}
                        >
                            Gerar
                        </Button>
                    </Form.Item>
                </Row>
            </Form>
        </div>        
    )


}

export const GenerateLeadsAI = () => {
  const [activeTab, setActiveTab] = useState('upload');

  const items = [
    {
      label: 'Upload',
      key: 'upload',
      children: <GenerateLeadsAIForm upload={true} keyId={`upload-${activeTab}`} />
    },
    {
      label: 'Manual',
      key: 'manual',
      children: <GenerateLeadsAIForm upload={false} keyId={`manual-${activeTab}`} />
    }
  ];

  return (
    <div className="main-container">
      <HeaderSection title={"Geração por IA"} />
      <RequirePermissions requiredPermission="ops_geracao_ia">
        <Tabs
          centered
          activeKey={activeTab}
          items={items}
          onChange={(key) => setActiveTab(key)}
        />
      </RequirePermissions>
    </div>
  )
}
